import { useState, type FormEvent } from "react";
import { Button, Input, Label, Textarea } from "@wondermaps/ui";
import { api } from "../../lib/api";

interface ReactivationRequestFormProps {
  orgSlug: string;
  mapSlug: string;
}

type FormState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success" }
  | { kind: "error"; message: string };

export function ReactivationRequestForm({
  orgSlug,
  mapSlug,
}: ReactivationRequestFormProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — never filled by humans
  const [state, setState] = useState<FormState>({ kind: "idle" });

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (state.kind === "submitting") return;
    setState({ kind: "submitting" });

    try {
      const res = await api.maps.visitor[":orgSlug"][":mapSlug"][
        "reactivation-request"
      ].$post({
        param: { orgSlug, mapSlug },
        json: { name, email, message: message || undefined, website },
      });
      if (!res.ok) {
        if (res.status === 429) {
          // Rate-limited — pretend success per plan, the lead is captured by IP
          setState({ kind: "success" });
          return;
        }
        const body = await res.json().catch(() => null);
        const errMsg =
          body && typeof body === "object" && "error" in body
            ? String((body as { error?: unknown }).error)
            : "Something went wrong";
        setState({ kind: "error", message: errMsg });
        return;
      }
      setState({ kind: "success" });
    } catch (err) {
      setState({
        kind: "error",
        message: err instanceof Error ? err.message : "Network error",
      });
    }
  }

  if (state.kind === "success") {
    return (
      <div
        className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900"
        role="status"
      >
        Thanks — Connor will be in touch shortly.
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <div>
        <Label htmlFor="reactivation-name">Name</Label>
        <Input
          id="reactivation-name"
          required
          maxLength={100}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="name"
        />
      </div>
      <div>
        <Label htmlFor="reactivation-email">Email</Label>
        <Input
          id="reactivation-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
        />
      </div>
      <div>
        <Label htmlFor="reactivation-message">Message (optional)</Label>
        <Textarea
          id="reactivation-message"
          maxLength={1000}
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
      </div>
      {/* Honeypot — invisible to humans, bots fill it. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-9999px",
          width: 1,
          height: 1,
          overflow: "hidden",
        }}
      >
        <label htmlFor="reactivation-website">Website</label>
        <input
          id="reactivation-website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>
      {state.kind === "error" && (
        <p className="text-sm text-red-600" role="alert">
          {state.message}
        </p>
      )}
      <Button type="submit" disabled={state.kind === "submitting"}>
        {state.kind === "submitting" ? "Sending…" : "Request to reactivate this map"}
      </Button>
    </form>
  );
}
